// src/components/order/ReservationItemTable.tsx
//
// ReservationItemTable
// - Shows reserved items for an order (SIM/eSIM reservation).
// - Excel export is treated as part of the Search UI concept (outside table review scope).
// - Uses StandardTableFrame + StandardPagination.
// - Handles empty values consistently with "-" display.
// - Client-side paging (reservationItems is already loaded in memory).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { utcToZonedTime } from 'date-fns-tz';
import { BarLoader } from 'react-spinners';

import {
  exportOrderReservationItemsExcel,
  type OrderReservationItem,
} from '@apis/order';
import { downloadBlob } from '@utils/file';
import { KOREAN_TIME_ZONE } from '@constants/timezone';

import {
  StandardPagination,
  StandardTableFrame,
  cellSx,
} from '@components/common/table';

type ReservationItemTableProps = {
  orderId: number;
  reservationItems: OrderReservationItem[];
};

const PAGE_SIZE = 50;

type CellValue = string | number | null | undefined;

const renderValue = (value: CellValue): React.ReactNode => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

export const ReservationItemTable = ({
  orderId,
  reservationItems,
}: ReservationItemTableProps) => {
  const theme = useTheme();

  const rows = useMemo(() => reservationItems ?? [], [reservationItems]);

  const totalCount = rows.length;

  const [page, setPage] = useState(1);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount],
  );

  // Clamp current page when totalCount/totalPages changes.
  useEffect(() => {
    setPage((prev) => Math.min(Math.max(prev, 1), totalPages));
  }, [totalPages]);

  const safePage = Math.min(Math.max(page, 1), totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting || totalCount === 0) return;

    setExporting(true);
    try {
      const res = await exportOrderReservationItemsExcel(orderId);
      const kstDate = utcToZonedTime(new Date(), KOREAN_TIME_ZONE);
      const fileName = `reservation-items-${orderId}-${kstDate.toISOString()}.xlsx`;
      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [isExporting, orderId, totalCount]);

  const handleChangePage = useCallback((next: number) => {
    setPage(next);
  }, []);

  return (
    <Card
      elevation={0}
      sx={{
        mt: 4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Reservations
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {totalCount.toLocaleString()} in total
          </Typography>
        }
        action={
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || totalCount === 0}
          >
            Export
          </Button>
        }
      />

      <CardContent>
        {isExporting ? (
          <Box mb={2}>
            <BarLoader loading width="100%" height={6} />
          </Box>
        ) : null}

        <Box mt={1}>
          <StandardTableFrame<OrderReservationItem>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Airport</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>P. Country</TableCell>
                <TableCell>P. Gender</TableCell>
                <TableCell>P. Name</TableCell>
                <TableCell>P. No.</TableCell>
              </TableRow>
            }
            rows={pageRows}
            getRowKey={(item) => String(item.orderReservationItemId)}
            columnsCount={9}
            isLoading={false}
            emptyText="No reservation items found."
            minWidth={900}
            renderRow={(item, index) => (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(safePage - 1) * PAGE_SIZE + index + 1}
                </TableCell>
                <TableCell>{renderValue(item.customerName)}</TableCell>
                <TableCell sx={cellSx.mono}>{renderValue(item.email)}</TableCell>
                <TableCell sx={cellSx.mono}>
                  {renderValue(item.airportCode)}
                </TableCell>
                <TableCell sx={cellSx.nowrap}>
                  {renderValue(item.itemStatus)}
                </TableCell>
                <TableCell>
                  {renderValue(item.passportData?.passportCountry)}
                </TableCell>
                <TableCell>
                  {renderValue(item.passportData?.passportGender)}
                </TableCell>
                <TableCell>
                  {renderValue(item.passportData?.passportName)}
                </TableCell>
                <TableCell sx={cellSx.mono}>
                  {renderValue(item.passportData?.passportNumber)}
                </TableCell>
              </>
            )}
          />
        </Box>

        <Box mt={2}>
          <StandardPagination
            page={safePage}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onChangePage={handleChangePage}
            size="small"
            shape="rounded"
          />
        </Box>
      </CardContent>
    </Card>
  );
};
